import { useState } from "react";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import FileUpload from "@/components/FileUpload";
import { UPDATE_JOB_APPLICATION_FILES_MUTATION } from "@/pages/people/[pgaId]/career/queries";

const ACCEPTED_FILE_TYPES = ".pdf";
const MAX_FILE_SIZE_MB = 10;

interface UpdateJobApplicationFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: string;
  onSuccess?: () => void;
}

export default function UpdateJobApplicationFilesModal({
  isOpen,
  onClose,
  applicationId,
  onSuccess
}: UpdateJobApplicationFilesModalProps) {
  const { close, Modal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const { setError } = useErrorBanner();

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [coverLetterFile, setCoverLetterFile] = useState<File | null>(null);
  const [fileUploadError, setFileUploadError] = useState<string>("");

  const [updateJobApplication, { loading }] = useMutation(UPDATE_JOB_APPLICATION_FILES_MUTATION, {
    refetchQueries: ["GetJobApplications", "JobPostWithApplications", "JobPostWithApplicationReviews"],
    awaitRefetchQueries: true
  });

  const handleSave = async () => {
    if (!resumeFile && !coverLetterFile) {
      setFileUploadError("Please select at least one file to replace");
      return;
    }

    try {
      const { data } = await updateJobApplication({
        variables: {
          input: {
            id: applicationId,
            ...(resumeFile && { resumeUpload: resumeFile }),
            ...(coverLetterFile && { coverLetterUpload: coverLetterFile })
          }
        }
      });

      if (data?.updateJobApplicationFiles?.success) {
        enqueueSnackbar(data.updateJobApplicationFiles.message || "Job application updated successfully");
        onSuccess?.();
        close();
      } else {
        setError(data?.updateJobApplicationFiles?.message || "Failed to update job application");
      }
    } catch (error: any) {
      setError(error.message || "An unexpected error occurred");
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      className="max-w-[660px] px-6 py-8"
      onClosed={onClose}
      headline="Replace Resume/Cover Letter"
      action={
        <Button variant="filled" onClick={handleSave} disabled={loading || (!resumeFile && !coverLetterFile)}>
          {loading ? "Uploading..." : "Save"}
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        {(!loading || resumeFile) && (
          <FileUpload
            overline="Resume"
            label="Upload a New Resume to Replace the Current File"
            subtitle={`Allowed file extensions: ${ACCEPTED_FILE_TYPES.replace(/\./g, "").replace(
              /,/g,
              ", "
            )}. Leave empty to keep current resume.`}
            accept={ACCEPTED_FILE_TYPES}
            maxSizeMB={MAX_FILE_SIZE_MB}
            loading={loading && !!resumeFile}
            handleUpload={(file) => {
              setResumeFile(file);
              setFileUploadError("");
            }}
            onError={(error) => setFileUploadError(error)}
          />
        )}

        {(!loading || coverLetterFile) && (
          <FileUpload
            overline="Cover Letter"
            label="Upload a New Cover Letter to Replace the Current File"
            subtitle={`Allowed file extensions: ${ACCEPTED_FILE_TYPES.replace(/\./g, "").replace(
              /,/g,
              ", "
            )}. Leave empty to keep current cover letter.`}
            accept={ACCEPTED_FILE_TYPES}
            maxSizeMB={MAX_FILE_SIZE_MB}
            loading={loading && !!coverLetterFile}
            handleUpload={(file) => {
              setCoverLetterFile(file);
              setFileUploadError("");
            }}
            onError={(error) => setFileUploadError(error)}
          />
        )}

        {fileUploadError && <div className="text-sys-brand-error text-body-medium">{fileUploadError}</div>}
      </div>
    </Modal>
  );
}
